import { APICaller } from '../hooks/useAPI';
import { APIType, FetchMethod } from '../hooks/types';
import AccountObject from '../models/accountObject/AccountObject';
import CashboxDetails from '../models/cashbox/CashboxDetails';
import EmployeeSchedule from '../models/employeeSchedule/EmployeeSchedule';
import OrderDetails from '../models/order/OrderDetails';
import PassengerDetails from '../models/passenger/PassengerDetails';
import ReportParams from '../models/report/ReportParams';
import Reward from '../models/reward/Reward';
import RouteDetails from '../models/route/RouteDetails';
import Schedule from '../models/schedule/Schedule';
import ScheduleExcursionDetails from '../models/schedule/ScheduleExcursionDetails';
import Collection from '../models/collection/Collection';
import Route from '../models/route/Route';
import { AccountType } from '../models/types';
import Excursion from '../models/excursion/Excursion';
import Price from '../models/price/Price';
import Order from '../models/order/Order';
import DiscountDetails from '../models/discount/DiscountDetails';
import RefundRequest from '../models/refund/RefundRequest';
import AccountObjectService from '../models/accountObject/AccountObjectService';

const API = {
  auth: {
    signIn: new APICaller('/auth/sign-in', FetchMethod.POST, 'auth', {
      body: { phone: { required: true }, password: { required: true } },
      auth: false, type: APIType.FETCH,
      errors: [ 'MISSING_REQUIRED_FIELDS', 'INVALID_PHONE', 'WRONG_CREDENTIALS' ]
    }),
    signOut: new APICaller('/auth/sign-out', FetchMethod.DELETE, 'auth', {
      body: {},
      auth: false, type: APIType.FETCH,
      errors: []
    }),
    checkPhone: new APICaller('/auth/check-phone', FetchMethod.POST, 'auth', {
      body: { phone: { required: true } },
      auth: false, type: APIType.FETCH,
      errors: [ 'MISSING_REQUIRED_FIELDS', 'INVALID_PHONE', 'USER_ALREADY_LOGGED_IN', 'USER_NOT_FOUND', 'USER_NO_PASSWORD', 'USER_HAS_PASSWORD' ]
    }),
    setPassword: new APICaller('/auth/set-password', FetchMethod.POST, 'auth', {
      body: { token: { required: true }, password: { required: true } },
      auth: false, type: APIType.FETCH,
      errors: [ 'MISSING_REQUIRED_FIELDS', 'INVALID_TOKEN', 'USER_NOT_FOUND' ]
    }),
  },
  user: {
    whoami: new APICaller('/user', FetchMethod.GET, 'users', {
      body: {},
      auth: false, type: APIType.FETCH,
      errors: []
    }),
    sendCode: new APICaller('/users/send-code', FetchMethod.POST, 'users', {
      body: {
        phone: { required: true },
        captcha: { required: true },
        lang: { required: false }
      },
      auth: false, type: APIType.FETCH,
      errors: [ 'INVALID_PHONE', 'INVALID_CAPTCHA', 'MISSING_REQUIRED_FIELDS' ]
    }),
    validateCode: new APICaller('/users/validate-code', FetchMethod.POST, 'users', {
      body: {
        phone: { required: true },
        code: { required: true }
      },
      auth: false, type: APIType.FETCH,
      errors: [ 'INVALID_PHONE', 'MISSING_REQUIRED_FIELDS' ]
    }),
    updateUser: new APICaller('/user', FetchMethod.PATCH, 'users', {
      body: {
        avatar: { required: false },
        nickname: { required: false },
        firstName: { required: false },
        lastName: { required: false },
        middleName: { required: false },
        birthday: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    changePassword: new APICaller('/user/change-password', FetchMethod.POST, 'users', {
      body: {
        currentPassword: { required: true },
        newPassword: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    uploadAvatar: new APICaller('/users/upload-avatar', FetchMethod.POST, 'users', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    tickets: new APICaller('/users/tickets', FetchMethod.GET, 'users', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    ticket: new APICaller('/misc/orders/:orderId/ticket', FetchMethod.GET, 'users', {
      body: {
        phone: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    autocompleteList: new APICaller('/accounts/:accountId/users/autocomplete-list', FetchMethod.GET, 'users', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    autocomplete: new APICaller('/accounts/:accountId/users/autocomplete', FetchMethod.GET, 'users', {
      body: {
        query: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    exists: new APICaller('/accounts/:accountId/users/exists', FetchMethod.GET, 'users', {
      body: {
        phone: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    user: new APICaller('/accounts/:accountId/users/:userId', FetchMethod.GET, 'users', {
      userId: { required: true },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  account: {
    account: new APICaller('/accounts/:accountId', FetchMethod.GET, 'accounts', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    users: new APICaller('/accounts/:accountId/users', FetchMethod.POST, 'accounts', {
      body: {
        filters: { required: false, fields: [
          'phone',
          'roles',
          'status', // `active`, `suspended`
          'language',
          'profile.lastName',
          'profile.firstName',
          'profile.middleName',
          'profile.fullName',
        ]},
        sort: { required: false, fields: [
          'phone',
          'account.status', // `active`, `suspended`
          'language',
          'profile.lastName',
          'profile.firstName',
          'profile.middleName',
          'profile.fullName',
        ] },
        page: { required: false },
        pageSize: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  point: {
    autocompleteList: new APICaller('/accounts/:accountId/points/autocomplete-list', FetchMethod.GET, 'points', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    report: new APICaller('/accounts/:accountId/points/report', FetchMethod.POST, 'points', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    point: new APICaller('/accounts/:accountId/points/:id', FetchMethod.GET, 'points', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  excursion: {
    autocomplete: new APICaller('/accounts/:accountId/excursions/autocomplete', FetchMethod.GET, 'excursions', {
      body: {
        query: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    autocompleteList: new APICaller('/accounts/:accountId/excursions/autocomplete-list', FetchMethod.GET, 'excursions', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    excursions: new APICaller('/accounts/:accountId/excursions/list/:date', FetchMethod.GET, 'excursions', { // YYYY-MM-DD
      body: {
        date: { required: true },
        timezone: { required: false }, // 'Europe/Moscow'
        subdivision: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Schedule
    }),
    excursions_shared: new APICaller('/accounts/:accountId/excursions/list-shared/:date', FetchMethod.GET, 'excursions-shared', { // YYYY-MM-DD
      body: {
        date: { required: true },
        timezone: { required: false }, // 'Europe/Moscow'
        subdivision: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Schedule
    }),
    excursion: new APICaller('/accounts/:accountId/excursions/:id', FetchMethod.GET, 'excursions', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Excursion
    }),
    report: new APICaller('/accounts/:accountId/excursions/report', FetchMethod.POST, 'excursions', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  geo: {
    getCountries: new APICaller('/misc/geos/countries', FetchMethod.GET, 'geos', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    address: new APICaller('/misc/geos/address-suggest', FetchMethod.GET, 'geos', {
      body: {
        address: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  subdivision: {
    subdivisions: new APICaller('/accounts/:accountId/subdivisions', FetchMethod.GET, 'subdivisions', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    subdivision: new APICaller('/accounts/:accountId/subdivisions/:id', FetchMethod.GET, 'subdivisions', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  price: {
    prices: new APICaller('/misc/prices', FetchMethod.GET, 'prices', {
      body: {
        tab: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Price
    }),
    price: new APICaller('/admin/prices/:id', FetchMethod.GET, 'prices', {
      body: {},
      auth: true, type: APIType.FETCH,
      accountType: AccountType.ADMIN,
      errors: []
    }),
  },
  place: {
    places: new APICaller('/accounts/:accountId/subdivisions/:id/places', FetchMethod.GET, 'places', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    place: new APICaller('/accounts/:accountId/places/:id', FetchMethod.GET, 'places', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  discount: {
    discounts: new APICaller('/accounts/:accountId/discounts/report', FetchMethod.POST, 'discounts', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    autocompleteList: new APICaller('/accounts/:accountId/discounts/autocomplete-list', FetchMethod.GET, 'discounts', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: DiscountDetails
    }),
    discount: new APICaller('/accounts/:accountId/discounts/:id', FetchMethod.GET, 'discounts', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  object: {
    accountObjects: new APICaller('/accounts/:accountId/objects/:id', FetchMethod.GET, 'objects', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: AccountObject
    }),
    services: new APICaller('/accounts/:accountId/objects/:id/services/report', FetchMethod.POST, 'objects', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: AccountObjectService
    }),
  },
  schedule: {
    schedule: new APICaller('/accounts/:accountId/schedules/:id', FetchMethod.GET, 'schedules', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Schedule
    }),
    autocompleteList: new APICaller('/accounts/:accountId/schedules/autocomplete-list', FetchMethod.GET, 'schedules', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: ScheduleExcursionDetails
    }),
    dates: new APICaller('/accounts/:accountId/excursions/:id/schedules/dates', FetchMethod.GET, 'schedules', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    times: new APICaller('/accounts/:accountId/excursions/:id/schedules/:date/times', FetchMethod.GET, 'schedules', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    report: new APICaller('/accounts/:accountId/schedules/report', FetchMethod.POST, 'schedules', {
      body: new ReportParams(),
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: ScheduleExcursionDetails
    }),
    datesSeats: new APICaller('/accounts/:accountId/excursions/:id/schedules/dates-seats', FetchMethod.GET, 'schedules', {
      body: {
        subdivision: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  order: {
    createOrder: new APICaller('/accounts/:accountId/orders', FetchMethod.POST, 'orders', {
      body: {
        user: { required: false },
        userInfo: { required: false },
        excursion: { required: true },
        subdivision: { required: true },
        schedule: { required: true },
        passengers: { required: true },
        sellerType: { required: false },
        pointId: { required: false },
        paymentMethod: { required: true },
        contactPhones: { required: false },
        wishes: { required: false },
        place: { required: false },
        address: { required: false },
        passports: { required: false },
        comment: { required: false },
        discountId: { required: false },
      },
      auth: true, type: APIType.FETCH,
      errors: [ 'MISSING_REQUIRED_FIELDS', 'ALREADY_EXISTS', 'INVALID_ADDRESS', 'INVALID_SUBDIVISION', 'INVALID_PLACE' ]
    }),
    orders: new APICaller('/accounts/:accountId/orders/report', FetchMethod.POST, 'orders', {
      body: new ReportParams({
        filters: ['number', 'date', 'place', 'point', 'subdivision', 'paymentDetails.method', 'status', 'schedule', 'excursion', 'schedule.status', 'schedule.date', 'transports._id'],
        sort: ['number', 'schedule.date', 'transports.number', 'paymentDetails.paidAmount', 'paymentDetails.method', 'status']
      }),
      auth: true, type: APIType.FETCH,
      errors: [],
      OrderDetails
    }),
    autocomplete: new APICaller('/accounts/:accountId/orders/clientAutocomplete', FetchMethod.GET, 'orders', {
      body: {
        query: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    order: new APICaller('/accounts/:accountId/orders/:id', FetchMethod.GET, 'orders', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Order
    }),
    update: new APICaller('/accounts/:accountId/orders/:id', FetchMethod.PATCH, 'orders', {
      body: {
        place: { required: false },
        address: { required: false },
        comment: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Order
    }),
    cashPayment: new APICaller('/accounts/:accountId/orders/:id/cash-payment', FetchMethod.POST, 'orders', {
      body: {
        amount: { required: true }, // сумма платежа
        comment: { required: false } // комментарий к платежу
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    nfcPayment: new APICaller('/accounts/:accountId/orders/:orderId/nfc-payment', FetchMethod.POST, 'orders', {
      body: {
        terminalKey: { required: true }, // ключ терминала с которого произведена оплата
        rrn: { required: true }, // ID платежа (rnn из колбека)
        mid: { required: true }, // Идентификатор торговой точки
        method: { required: true }, // nfc или qr
        transactionDate: { required: true }, // дата и время платежа из колбека!
        amount: { required: true }, // сумма платежа
        comment: { required: false }, // комментарий к платежу - **необязательный параметр**
        orderId: { required: true },
      },
      auth: true, type: APIType.FETCH,
      errors: [ 'PAYMENT_ALREADY_EXISTS', 'ORDER_ALREADY_PAID', 'INVALID_AMOUNT', 'OPERATION_NOT_FOUND' ]
    }),
    cancel: new APICaller('/accounts/:accountId/roles/cashiers/orders/:orderId/cancel', FetchMethod.DELETE, 'orders', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
    }),
    refund: new APICaller('/accounts/:accountId/orders/:id/refund', FetchMethod.POST, 'orders', {
      body: {
        amounts: { required: false }, // { _id, prepayAmount, usePrepayRefundPolicy, postpayAmount }
        refundPolicyRule: { required: false },
        cancelOrder: { required: false },
        reason: { required: false },
        prepayMethod: { required: false },
        prepayUser: { required: false },
        postpayMethod: { required: false },
        postpayUser: { required: false },
        saveSalary: { required: false },
        images: { required: false },
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    partialRefund: new APICaller('/accounts/:accountId/orders/:id/partial-refund', FetchMethod.POST, 'orders', {
      body: {
        amounts: { required: false }, // { prepayAmount, postpayAmount }
        reason: { required: false },
        prepayMethod: { required: false },
        prepayUser: { required: false },
        postpayMethod: { required: false },
        postpayUser: { required: false },
        saveSalary: { required: false },
        images: { required: false },
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  cashier: {
      orders: new APICaller('/accounts/:accountId/roles/cashiers/orders', FetchMethod.GET, 'cashiers', {
          body: {
              client: { required: false },
              date: { required: false }
          },
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: OrderDetails
      }),
      ordersReport: new APICaller('/accounts/:accountId/roles/cashiers/orders-report', FetchMethod.POST, 'cashiers', {
          body: new ReportParams({
              filters: ['date', 'user', 'number'],
              sort: ['date'],
              page: Number
          }),
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: OrderDetails
      }),

      ordersUncollectedReport: new APICaller('/accounts/:accountId/roles/cashiers/uncollected-orders', FetchMethod.POST, 'cashiers', {
          body: new ReportParams({
              filters: ['date', 'user', 'number'],
              sort: ['date'],
              page: Number
          }),
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: OrderDetails
      }),
    passengers: new APICaller('/accounts/:accountId/roles/cashiers/passengers', FetchMethod.GET, 'cashiers', {
      body: {
        date: { required: false }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: PassengerDetails
    }),
    setPassengers: new APICaller('/accounts/:accountId/roles/cashiers/passengers', FetchMethod.POST, 'cashiers', {
      body: {
        order: { required: true }, //ID
        seat: { required: true }, //boolean
        place: { required: true }, //ID
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: PassengerDetails
    }),
    schedules: new APICaller('/accounts/:accountId/roles/cashiers/schedules', FetchMethod.GET, 'cashiers', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: EmployeeSchedule
    }),
    cashbox: new APICaller('/accounts/:accountId/roles/cashiers/cashbox', FetchMethod.GET, 'cashiers', {
      body: {
        fromDate: { required: true }, //YYYY-MM-DD
        toDate: { required: true }, //YYYY-MM-DD
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: CashboxDetails
    }),
    salaries: new APICaller('/accounts/:accountId/roles/cashiers/salaries', FetchMethod.GET, 'cashiers', {
      body: {
        fromDate: { required: false }, //YYYY-MM-DD
        toDate: { required: false }, //YYYY-MM-DD
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Reward
    }),
    withdrawSalary: new APICaller('/accounts/:accountId/roles/cashiers/withdraw-salary', FetchMethod.POST, 'cashiers', {
      body: {
        amount: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: [],
    }),
    createCollection: new APICaller('/accounts/:accountId/roles/cashiers/collections', FetchMethod.POST, 'cashiers', {
          body: {
              amount: { required: true },
              orders: { required: true }
          },
          auth: true, type: APIType.FETCH,
          errors: [],
    }),
    collectionsReport: new APICaller('/accounts/:accountId/roles/cashiers/collections-list', FetchMethod.POST, 'cashiers', {
      body: new ReportParams({
        sort: ['date'],
        page: Number
      }),
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Collection
    }),
    getCollection: new APICaller('/accounts/:accountId/roles/cashiers/collection/:id', FetchMethod.GET, 'cashiers', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Collection
    }),
    cancelSalary: new APICaller('/accounts/:accountId/roles/cashiers/withdraw-salary', FetchMethod.DELETE, 'cashiers', {
      body: {
        id: { required: true },
        amount: { required: true },
      },
      auth: true, type: APIType.FETCH,
      errors: [],
    }),
      refundRequests: new APICaller('/accounts/:accountId/roles/cashiers/refund-requests', FetchMethod.GET, 'cashiers', {
          body: {},
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: RefundRequest
      }),
      refundRequestsReport: new APICaller('/accounts/:accountId/roles/cashiers/refund-requests-report', FetchMethod.POST, 'cashiers', {
          body: new ReportParams({
              filters: ['date', 'user', 'number'],
              sort: ['date'],
              page: Number
          }),
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: RefundRequest
      }),
    refundRequest: new APICaller('/accounts/:accountId/roles/cashiers/refund-requests/:id', FetchMethod.GET, 'cashiers', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: RefundRequest
    }),
    completeRefundRequest: new APICaller('/accounts/:accountId/roles/cashiers/refund-requests/:id/complete', FetchMethod.POST, 'cashiers', {
      body: {
        amount: { required: true }
      },
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
  guide: {
    schedules: new APICaller('/accounts/:accountId/roles/guides/schedules', FetchMethod.GET, 'guides', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: RouteDetails
    }),
    schedule: new APICaller('/accounts/:accountId/roles/guides/orders/:scheduleId', FetchMethod.GET, 'guides', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Order
    }),
    seat: new APICaller('/accounts/:accountId/roles/guides/orders/:orderId/seat', FetchMethod.POST, 'guides', {
      body: {
        transport: { required: true },
        seats: { required: true },
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Object
    }),
  },
  collectors: {
    collections: new APICaller('/accounts/:accountId/roles/collectors/collections', FetchMethod.GET, 'collectors', {
      body: {
        fromDate: { required: true }, //YYYY-MM-DD
        toDate: { required: true }, //YYYY-MM-DD
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Collection
    }),
    createCollection: new APICaller('/accounts/:accountId/roles/collectors/collections', FetchMethod.POST, 'collectors', {
      body: {
        user: { required: true },
        amount: { required: true },
      },
      auth: true, type: APIType.FETCH,
      errors: [],
      clazz: Collection
    }),
    approveCollection: new APICaller('/accounts/:accountId/roles/collectors/collections/:id/approve', FetchMethod.POST, 'collectors', {
          body: {},
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: Collection
    }),
    rejectCollection: new APICaller('/accounts/:accountId/roles/collectors/collections/:id/reject', FetchMethod.POST, 'collectors', {
          body: {},
          auth: true, type: APIType.FETCH,
          errors: [],
          clazz: Collection
      }),
  },
  route: {
    routes: new APICaller('/accounts/:accountId/routes/report', FetchMethod.POST, 'routes', {
      body: new ReportParams({
        filters: ['title', 'status', 'subdivisions.subdivision'],
        sort: ['title', 'status']
      }),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
    route: new APICaller('/accounts/:accountId/routes/:id', FetchMethod.GET, 'routes', {
      body: {},
      auth: true, type: APIType.FETCH,
      errors: [],
      Route
    }),
  },
  bonus: {
    bonuses: new APICaller('/accounts/:accountId/bonuses/report', FetchMethod.POST, 'bonuses', {
      body: new ReportParams({
        filters: ['title'],
        sort: ['title']
      }),
      auth: true, type: APIType.FETCH,
      errors: []
    }),
  },
};

export default API;